import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Badge } from '../components/ui/Badge';
import { apiClient } from '../lib/api/client';

interface CallSummary {
  call_sid: string;
  created_at?: string;
  duration_seconds: number;
  status: string;
  has_transcription: boolean;
  has_audit_report: boolean;
  quality_score?: number;
  audio_url?: string;
}

interface ConsolidatedSummary {
  available: boolean;
  session_id?: string;
  merged_at?: string;
  call_count: number;
  total_words: number;
  audit_report?: {
    report_id: string;
    score: number;
    grade: string;
    status: string;
  };
  sga_report?: any;
}

interface CandidateCallHistory {
  candidate_id: string;
  candidate_name: string;
  total_calls: number;
  calls: CallSummary[];
  consolidated: ConsolidatedSummary;
}

export const CandidateCallHistoryPage: React.FC = () => {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CandidateCallHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [transcribingCalls, setTranscribingCalls] = useState<Set<string>>(new Set());
  const [consolidating, setConsolidating] = useState(false);
  const [consolidationStatus, setConsolidationStatus] = useState<'none' | 'processing' | 'completed'>('none');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasCheckedConsolidationRef = useRef(false);

  useEffect(() => {
    hasCheckedConsolidationRef.current = false;
    stopPolling();
    fetchCallHistory();
  }, [candidateId]);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  };

  const fetchCallHistory = async () => {
    try {
      setLoading(true);
      const response = await apiClient.client.get(`/api/v1/candidates/${candidateId}/call-history`);
      setData(response.data);
      const calls: CallSummary[] = response.data.calls || [];
      const callSids = calls.map((c: CallSummary) => c.call_sid).filter(Boolean);
      
      // Check consolidation status
      if (response.data.consolidated && response.data.consolidated.available) {
        setConsolidationStatus('completed');
        setSessionId(response.data.consolidated.session_id);
        hasCheckedConsolidationRef.current = false;
        stopPolling();
      } else if (callSids.length >= 2) {
        // Check if there's an ongoing consolidation
        if (!hasCheckedConsolidationRef.current && consolidationStatus !== 'processing') {
        try {
          const statusResponse = await apiClient.client.post(`/api/v1/candidates/${candidateId}/consolidate-calls`, {
              call_sids: callSids
          });
          
          if (statusResponse.data.status === 'completed') {
            setConsolidationStatus('completed');
            setSessionId(statusResponse.data.session_id);
              hasCheckedConsolidationRef.current = false;
              stopPolling();
          } else if (statusResponse.data.status === 'processing') {
            setConsolidationStatus('processing');
            setSessionId(statusResponse.data.session_id);
              hasCheckedConsolidationRef.current = true;
              if (statusResponse.data.session_id) {
                startPolling(statusResponse.data.session_id, callSids);
              }
          }
        } catch (e) {
          // Ignore errors when checking status
          console.log('No existing consolidation');
        }
        }
      } else {
        // Not enough calls to consolidate
        setConsolidationStatus('none');
        setSessionId(null);
        hasCheckedConsolidationRef.current = false;
        stopPolling();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch call history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getQualityColor = (score?: number) => {
    if (!score) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    if (score >= 70) return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200';
    if (score >= 40) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200';
    return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200';
  };

  const getGradeColor = (grade: string) => {
    const gradeMap: Record<string, string> = {
      'A': 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
      'B': 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
      'C': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200',
      'D': 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200',
      'F': 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200'
    };
    return gradeMap[grade] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  const handleTranscribeCall = async (callSid: string) => {
    try {
      setTranscribingCalls(prev => new Set(prev).add(callSid));
      await apiClient.client.post(`/api/v1/calls/${callSid}/transcribe`);
      alert('Transcription started! Please wait 2-3 minutes and refresh.');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start transcription');
    } finally {
      setTranscribingCalls(prev => {
        const newSet = new Set(prev);
        newSet.delete(callSid);
        return newSet;
      });
    }
  };

  const startPolling = (pollingSessionId: string, callSids: string[]) => {
    if (!pollingSessionId) return;
    if (pollingIntervalRef.current) {
      return;
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const statusResponse = await apiClient.client.post(`/api/v1/candidates/${candidateId}/consolidate-calls`, {
          call_sids: callSids
        });

        if (statusResponse.data.status === 'completed') {
          stopPolling();
          hasCheckedConsolidationRef.current = false;
          setConsolidationStatus('completed');
          setSessionId(pollingSessionId);
          alert('✅ Analysis completed! The "View Report" button is now available.');
          fetchCallHistory();
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 10000);

    pollingTimeoutRef.current = setTimeout(() => {
      stopPolling();
    }, 300000);
  };

  const handleConsolidate = async () => {
    try {
      setConsolidating(true);
      const callSids = (data?.calls || []).map(c => c.call_sid).filter(Boolean);
      const response = await apiClient.client.post(`/api/v1/candidates/${candidateId}/consolidate-calls`, {
        call_sids: callSids
      });
      
      const status = response.data.status;
      const respSessionId = response.data.session_id;
      
      if (status === 'transcribing') {
        alert(response.data.message);
      } else if (status === 'completed') {
        // Analysis is already complete
        setConsolidationStatus('completed');
        setSessionId(respSessionId);
        alert('Analysis already completed! Click "View Report" to see results.');
      } else if (status === 'processing') {
        // Analysis in progress, start polling
        setConsolidationStatus('processing');
        setSessionId(respSessionId);
        alert(response.data.message + ' The button will update when complete.');
        stopPolling();
        hasCheckedConsolidationRef.current = true;
        if (respSessionId) {
          // Poll for completion
          startPolling(respSessionId, callSids);
        }
      }
      
      // Refresh data
      fetchCallHistory();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start consolidation');
    } finally {
      setConsolidating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Failed to load data'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Call History - {data.candidate_name}
            </h1>
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">{data.total_calls} total calls</p>
              {consolidationStatus !== 'none' && (
                <Badge
                  variant={consolidationStatus === 'completed' ? 'default' : 'outline'}
                  className="px-2 py-0.5 text-xs"
                >
                  {consolidationStatus === 'completed' ? 'Report Ready' : 'Analysis in Progress'}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {data.total_calls >= 2 && (
              <>
                {consolidationStatus === 'completed' && sessionId ? (
                  <button
                    onClick={() => navigate(`/audit-reports/consolidated/${sessionId}`)}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                  >
                    📊 View Report
                  </button>
                ) : (
              <button
                onClick={handleConsolidate}
                    disabled={consolidating || consolidationStatus === 'processing'}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                    {consolidationStatus === 'processing' 
                      ? '⏳ Analysis in Progress...' 
                      : consolidating 
                        ? '⏳ Starting...' 
                        : '🔗 Merge & Analyze All Calls'}
              </button>
                )}
              </>
            )}
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* Consolidated Analysis Section */}
      {data.consolidated.available && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-700 rounded-xl p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                🔗 Consolidated Analysis
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                  Available
                </span>
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Combined analysis of {data.consolidated.call_count} calls
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur rounded-lg p-4">
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Calls</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                {data.consolidated.call_count}
              </div>
            </div>

            <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur rounded-lg p-4">
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Words</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                {data.consolidated.total_words}
              </div>
            </div>

            {data.consolidated.audit_report && (
              <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur rounded-lg p-4">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Quality Score</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {data.consolidated.audit_report.score}/100
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getGradeColor(data.consolidated.audit_report.grade)}`}>
                    Grade {data.consolidated.audit_report.grade}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={() => navigate(`/transcriptions/merged/${data.consolidated.session_id}`)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              📝 View Merged Transcription
            </button>
            {data.consolidated.audit_report && (
              <button
                onClick={() => navigate(`/audit-reports/consolidated/${data.consolidated.session_id}`)}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                📊 View Audit Report
              </button>
            )}
            {data.consolidated.sga_report && (
              <button
                onClick={() => navigate(`/sga-reports/consolidated/${data.consolidated.session_id}`)}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
              >
                🎯 View SGA Report
              </button>
            )}
          </div>

          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Merged: {formatDate(data.consolidated.merged_at)}
          </div>
        </div>
      )}

      {/* Individual Calls Section */}
      <div className="bg-white dark:bg-gray-800/90 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Individual Calls</h2>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {data.calls.map((call, index) => (
            <div key={call.call_sid} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      📞 Call #{data.calls.length - index}
                    </span>
                    <Badge variant="default">{call.status}</Badge>
                    {call.quality_score !== undefined && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getQualityColor(call.quality_score)}`}>
                        {call.quality_score}/100
                      </span>
                    )}
                  </div>

                  <div className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-300">
                    <div>📅 {formatDate(call.created_at)}</div>
                    <div>⏱️ Duration: {call.duration_seconds > 0 ? `${call.duration_seconds}s` : 'N/A'}</div>
                    <div className="flex items-center gap-2">
                      {call.has_transcription ? (
                        <span className="text-green-600 dark:text-green-400">✓ Transcribed</span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">⊗ No transcription</span>
                      )}
                      {call.has_audit_report && (
                        <span className="text-green-600 dark:text-green-400">• ✓ Audit Available</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  {/* View Transcription button - show if transcribed */}
                  {call.has_transcription && (
                    <button
                      onClick={() => navigate(`/transcriptions/session_${call.call_sid}`)}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                    >
                      📄 View Transcription
                    </button>
                  )}
                  
                  {/* Transcribe button - only show if not transcribed */}
                  {!call.has_transcription && (
                    <button
                      onClick={() => handleTranscribeCall(call.call_sid)}
                      disabled={transcribingCalls.has(call.call_sid)}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      {transcribingCalls.has(call.call_sid) ? '⏳ Transcribing...' : '📝 Transcribe'}
                    </button>
                  )}
                  
                  {/* View audit button - only show if audit exists */}
                  {call.has_audit_report && (
                    <button
                      onClick={() => navigate(`/audit-reports/${call.call_sid}`)}
                      className="px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-900/50 rounded hover:bg-indigo-100 dark:hover:bg-indigo-800/50 transition-colors"
                    >
                      📊 View Audit
                    </button>
                  )}
                  
                  {/* Hint to transcribe first if transcription not available but user might want it */}
                  {!call.has_transcription && !transcribingCalls.has(call.call_sid) && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                      Transcribe to enable analysis
                    </span>
                  )}
                </div>
              </div>

              {/* Inline audio player if available */}
              {call.audio_url && (
                <div className="mt-4">
                  <audio controls preload="none" className="w-full">
                    <source src={call.audio_url} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}

              <div className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                Call SID: {call.call_sid}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

